import { useCallback, useRef, useState } from 'react';
import type { Editor } from '@tiptap/react';
import type { Selection } from '@tiptap/pm/state';
import type { Node as PMNode } from '@tiptap/pm/model';

/**
 * Open/close state for a named-list menu (#358). Remembers the editor's selection
 * when the menu opens; closing without acting (tap outside, Escape) puts focus
 * and that selection back, as long as the document has not changed meanwhile.
 */
export function useNamedListMenu(editor: Editor) {
  const [open, setOpen] = useState(false);
  const saved = useRef<{ doc: PMNode; selection: Selection } | null>(null);

  const toggle = useCallback(() => {
    if (!open) saved.current = { doc: editor.state.doc, selection: editor.state.selection };
    setOpen(!open);
  }, [editor, open]);

  const close = useCallback(() => { setOpen(false); }, []);

  const dismiss = useCallback(() => {
    setOpen(false);
    const s = saved.current;
    saved.current = null;
    if (editor.isDestroyed) return;
    if (s && s.doc === editor.state.doc && !editor.state.selection.eq(s.selection)) {
      editor.view.dispatch(editor.state.tr.setSelection(s.selection));
    }
    editor.view.focus();
  }, [editor]);

  return { open, toggle, close, dismiss };
}
